/**
 * Utility functions to test connection to the backend
 */
import config from '../../config';

/**
 * Test connection to the backend
 * @returns {Promise<boolean>} Whether the connection was successful
 */
export const testBackendConnection = async () => {
  try {
    // Try the debug endpoint first
    const debugUrl = `${config.api.baseUrl}/debug/frontend-test`;
    
    try {
      const debugResponse = await fetch(debugUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        mode: 'cors'
      });
      
      if (debugResponse.ok) {
        const data = await debugResponse.json();
        console.log('Debug connection test successful:', data);
        return true;
      }
    } catch (debugError) {
      // Debug endpoint failed - this is expected if backend is down
      console.log('Debug endpoint unavailable, trying boards endpoint...');
    }
    
    // Fall back to the boards endpoint
    const url = `${config.api.baseUrl}/boards`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Origin': window.location.origin
      },
      mode: 'cors'
    });
    
    // Check if response is ok (status in the range 200-299)
    if (response.ok) {
      const data = await response.json();
      
      // Check if the response is an array (expected for boards endpoint)
      if (Array.isArray(data)) {
        return true;
      } else {
        console.warn('Boards endpoint returned non-array response:', data);
        return false;
      }
    } else {
      console.log('Backend returned status:', response.status, '- working in local mode');
      return false;
    }
  } catch (error) {
    console.log('Backend unavailable - working in local mode');
    return false;
  }
};

/**
 * Check if the MongoDB database is available
 * @returns {Promise<boolean>} Whether MongoDB is available
 */
export const testMongoDBConnection = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    // Try the db-status endpoint first
    try {
      const response = await fetch(`${config.api.baseUrl}/db-status`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        mode: 'cors',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        return data.connected === true;
      }
    } catch (err) {
      console.warn('DB status endpoint not available, trying alternative methods');
    }
    
    // If db-status fails, try boards as a fallback
    try {
      const response = await fetch(`${config.api.baseUrl}/boards`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        mode: 'cors',
        signal: controller.signal
      });
      
      // If we can fetch boards, assume the database is working
      return response.ok;
    } catch (err) {
      console.warn('Could not connect to boards endpoint');
      return false;
    }
  } catch (error) {
    console.error('MongoDB connection test failed:', error);
    return false;
  }
};

export default {
  testBackendConnection,
  testMongoDBConnection
}; 